import { z } from "zod";

//
// Protocol
//
// We need "primitive" business types here as having a dependency on Helios for
// indexing, DB representation, and FE displaying, etc. is not ideal.
// Ideally, only `kreate-protocol` imports from Helios, and
// everywhere else imports these `kreate-common` types.

// Preferably, we only support Mainnet & Preprod.
// However, both Preprod & Preview can break every now and then.
// We support both to switch between for (higher chance of) undisrupted development.
export const CardanoNetwork = z.union([
  z.literal("Preview"),
  z.literal("Preprod"),
  z.literal("Mainnet"),
]);
export type CardanoNetwork = z.infer<typeof CardanoNetwork>;

export const CardanoAddress = z.string();
export type CardanoAddress = z.infer<typeof CardanoAddress>;

export const TokenName = z.string();
export type TokenName = z.infer<typeof TokenName>;

export const CardanoToken = z.object({
  tokenName: TokenName,
  tokenValue: z.number(),
});
export type CardanoToken = z.infer<typeof CardanoAsset>;

export const CardanoAsset = z.object({
  mintingPolicyHash: z.string(),
  tokens: CardanoToken.array(),
});
export type CardanoAsset = z.infer<typeof CardanoAsset>;

export const DatumProtocolParams = z.object({
  modPKH: z.string(),
  treasuryAddress: CardanoAddress,
  kreatorMintFee: z.number(),
});
export type DatumProtocolParams = z.infer<typeof DatumProtocolParams>;

export const DatumArtwork = z.object({
  ownerAddress: CardanoAddress,
  collectionId: z.string(),
  tokenName: TokenName,
  mintFee: z.number(),
  numEditions: z.number(),
  numMinted: z.number(),
});
export type DatumArtwork = z.infer<typeof DatumArtwork>;

export const DatumMarketplace = z.object({
  sellerAddress: CardanoAddress,
  assetId: z.string(),
  editionPrice: z.number(),
});
export type DatumMarketplace = z.infer<typeof DatumMarketplace>;

export const DatumSecondary = z.object({
  sellerAddress: CardanoAddress,
  assetId: z.string(),
  editionPrice: z.number(),
  artistAddress: CardanoAddress.nullable(),
  editionRoyalty: z.number(),
});
export type DatumSecondary = z.infer<typeof DatumSecondary>;

export const DatumOffer = z.object({
  offererAddress: CardanoAddress,
  assetId: z.string(),
  editionPrice: z.number(),
  wantedQuantity: z.number(),
  isSecondary: z.boolean(),
  artistAddress: CardanoAddress.nullish(),
  editionRoyalty: z.number(),
});
export type DatumOffer = z.infer<typeof DatumOffer>;

export const DatumAuctionEnglish = z.object({
  ownerAddress: CardanoAddress,
  deadline: z.number(), // unix ms
  reserve: z.number(),
  increment: z.number(),
  bidderAddress: CardanoAddress,
});
export type DatumAuctionEnglish = z.infer<typeof DatumAuctionEnglish>;

// TODO: Use generics?
export const KreateDatum = z.union([
  z.object({
    type: z.literal("protocol-params"),
    value: DatumProtocolParams,
  }),
  z.object({
    type: z.literal("artwork"),
    value: DatumArtwork,
  }),
  z.object({
    type: z.literal("marketplace"),
    value: DatumMarketplace,
  }),
  z.object({
    type: z.literal("secondary"),
    value: DatumSecondary,
  }),
  z.object({
    type: z.literal("offer"),
    value: DatumOffer,
  }),
  z.object({
    type: z.literal("auction-english"),
    value: DatumAuctionEnglish,
  }),
]);
export type KreateDatum = z.infer<typeof KreateDatum>;

export const KreateUTxO = z.object({
  txId: z.string(),
  txIx: z.number(),
  address: CardanoAddress,
  lovelaceValue: z.number(),
  otherAssets: CardanoAsset.array(),
  datum: KreateDatum,
  sellerStakeAddress: z.string().nullish(),
  offererStakeAddress: z.string().nullish(),
});
export type KreateUTxO = z.infer<typeof KreateUTxO>;

//
// End of Protocol
//

export const NonceTokenString = z.string().regex(/^\d{24}$/);
export type NonceTokenString = z.infer<typeof NonceTokenString>;

export const ObjectIdAsHexString = z.string().regex(/^[0-9a-f]{24}$/);
export type ObjectIdAsHexString = z.infer<typeof ObjectIdAsHexString>;

export const UrlHandleString = z
  .string()
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{1,62}[a-zA-Z0-9]$/);
export type UrlHandleString = z.infer<typeof UrlHandleString>;

export const UrlAdaHandleString = z.string().regex(/^[.a-zA-Z0-9_-]{1,15}$/);
export type UrlAdaHandleString = z.infer<typeof UrlAdaHandleString>;

export const CollectionUrlHandleString = z
  .string()
  .regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{1,21}[a-zA-Z0-9]$/);
export type CollectionUrlHandleString = z.infer<
  typeof CollectionUrlHandleString
>;

export const GalleryAlbumUrlHandleString = CollectionUrlHandleString;
export type GalleryAlbumUrlHandleString = CollectionUrlHandleString;

export const MintingMethod = z.enum([
  "on-demand-minting",
  "reservation-based-minting",
]);
export type MintingMethod = z.infer<typeof MintingMethod>;

export const UserId = z.string();
export type UserId = z.infer<typeof UserId>;

export const UnixTimestamp = z.number().max(2199023255551); // milliseconds
export type UnixTimestamp = z.infer<typeof UnixTimestamp>;

export const CONTENT_TYPE = {
  image: [
    "image/gif",
    "image/jpg",
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/svg+xml",
  ],
  video: ["video/mp4"],
  text: ["text/html"],
} as const;

export const ContentType = z.enum([
  ...CONTENT_TYPE.image,
  ...CONTENT_TYPE.video,
  ...CONTENT_TYPE.text,
]);
export type ContentType = z.infer<typeof ContentType>;

export const BlobId = z.string();
export type BlobId = z.infer<typeof BlobId>;

// BlobHandle - a blob id and its metadata
export const BlobHandle = z.object({
  blobId: BlobId, // primary key
  createdAt: UnixTimestamp,
  contentType: ContentType,
  size: z.number().optional(), // bytes - content size
  width: z.number().optional(), // pixels - image natural width
  height: z.number().optional(), // pixels - image natural height
});

export type BlobHandle = z.infer<typeof BlobHandle>;

export const FileId = z.string();
export type FileId = z.infer<typeof FileId>;

// FileHandle - an BlobHandle with alternative versions
export const FileHandle = z.object({
  fileId: FileId, // primary key
  createdAt: UnixTimestamp,
  createdBy: UserId,
  blob: BlobHandle,
  alternativeBlobIds: BlobId.array(), // auto-generated versions with smaller resolution
  ipfsHash: z.string().nullish(),
  // default image file generated by Kreate
  systemDefaultImageFileId: z.string().nullish(),
});

export type FileHandle = z.infer<typeof FileHandle>;

export const AssetOnChainData = z.object({
  policyId: z.string(),
  assetId: z.string(),
  quantity: z.number().nullish(),
});

export type AssetOnChainData = z.infer<typeof AssetOnChainData>;

export const PayoutMethod = z.union([
  z.object({
    type: z.literal("bank-transfer"),
    bankName: z.string().nullish(),
    accountName: z.string().nullish(),
    accountNumber: z.string().nullish(),
  }),
  z.object({
    type: z.literal("momo"),
    accountName: z.string().nullish(),
    phoneNumber: z.string().nullish(),
  }),
  z.object({
    type: z.literal("crypto"),
    chainName: z.string().nullish(),
    tokenName: z.string().nullish(),
    address: z.string().nullish(),
  }),
]);

export type PayoutMethod = z.infer<typeof PayoutMethod>;

export const SocialLink = z.string();

export type SocialLink = z.infer<typeof SocialLink>;

export const PolicyRoyalty = z.object({
  policyId: z.string(),
  artistAddress: z.string(),
  royaltyRate: z.number(), // 0-1
  source: z.union([z.literal("manual"), z.literal("cip27")]),
  isNull: z.boolean().nullish(),
});
export type PolicyRoyalty = z.infer<typeof PolicyRoyalty>;

export const BaseGalleryInfo = z.object({
  title: z.string().nullish(),
  description: z.string().nullish(),
  avatarAssetId: z.string().nullish(),
  landingAlbumId: ObjectIdAsHexString.nullish(),
  customFrame: z.string().nullish(),
  updatedAt: UnixTimestamp.nullish(),
});
export type BaseGalleryInfo = z.infer<typeof BaseGalleryInfo>;

export const GalleryInfo = BaseGalleryInfo.extend({
  avatar: FileHandle.nullish(),
  socialLinks: SocialLink.array().nullish(),
});
export type GalleryInfo = z.infer<typeof GalleryInfo>;

export const RoyaltySettings = z.object({
  royaltyRate: z.number(),
  artistAddress: z.string(),
});
export type RoyaltySettings = z.infer<typeof RoyaltySettings>;

export const BaseUser = z.object({
  userId: UserId,
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp.nullish(),
  displayName: z.string().nullish(),
  headline: z.string().nullish(),
  aboutDescription: z.string().nullish(),
  avatar: FileHandle.nullish(),
  banner: FileHandle.nullish(),
  // We only return payout methods for the logged-in user
  payoutMethods: PayoutMethod.array().nullish(),
  socialLinks: SocialLink.array(),
  lovedBy: UserId.array(),
  tags: z.string().array().nullish(),
  // Artists can delegate management tasks to the platform agents
  isAgent: z.boolean(),
  acceptAgents: z.boolean(),
  isVerified: z.boolean().nullish(),
  flagWarning: z.boolean().nullish(),
  flagSuspended: z.boolean().nullish(),
  handle: UrlHandleString.nullish(),
  customUnsortedCollectionName: z.string().nullish(),
  walletAddresses: z.string().array().nullish(),
  // notification
  subcribeEmailNotification: z.boolean().nullish(),
  // gallery
  galleryInfo: GalleryInfo.nullish(),
  // artist default royalty for secondary marketplace
  defaultPolicyRoyalty: RoyaltySettings.nullish(),
});

export type BaseUser = z.infer<typeof BaseUser>;

export const SimpleUser = BaseUser.pick({
  displayName: true,
  userId: true,
  headline: true,
  aboutDescription: true,
  avatar: true,
  handle: true,
  isVerified: true,
  acceptAgents: true,
  isAgent: true,
  flagWarning: true,
  flagSuspended: true,
  customUnsortedCollectionName: true,
  walletAddresses: true,
  socialLinks: true,
});

export type SimpleUser = z.infer<typeof SimpleUser>;

export const BaseCollection = z.object({
  collectionId: z.string(), // primary key
  createdBy: UserId,
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp.nullish(),
  title: z.string(),
  description: z.string().nullish(),
  avatar: FileHandle.nullish(),
  banner: FileHandle.nullish(),
  tags: z.string().array(),
  isNSFW: z.boolean(),
  lovedBy: UserId.array(),
  handle: CollectionUrlHandleString.nullish(),
  gachaMinting: z
    .object({
      numRemainingTokens: z.number().nullish(), // calculated by system
      totalTokens: z.number().nullish(),
      editionPrice: z.number().nullish(),
      startedAt: UnixTimestamp.nullish(),
      endedAt: UnixTimestamp.nullish(),
      description: z.string().nullish(),
    })
    .nullish(),
  // restore policies
  policyId: z.string().nullish(),
  isPolicyLocked: z.boolean().nullish(),
});

export type BaseCollection = z.infer<typeof BaseCollection>;

export const SimpleCollection = BaseCollection.pick({
  collectionId: true,
  title: true,
  description: true,
  policyId: true,
  handle: true,
  avatar: true,
});

export type SimpleCollection = z.infer<typeof SimpleCollection>;

// Curated collection
// collection: `curated_collection`
export const BaseCuratedCollection = z.object({
  collectionId: z.string(), // primary key
  createdBy: UserId,
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp.nullish(),
  title: z.string(),
  description: z.string().nullish(),
  avatarFileId: ObjectIdAsHexString.nullish(),
  bannerFileId: ObjectIdAsHexString.nullish(),
  // TODO: support love collection
  // lovedBy: UserId.array(),
  handle: CollectionUrlHandleString.nullish(),
});
export type BaseCuratedCollection = z.infer<typeof BaseCuratedCollection>;

export const CuratedCollectionArtworkEntry = z.object({
  artworkId: ObjectIdAsHexString,
  collectionId: ObjectIdAsHexString,
  priority: z.number().nullish(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});
export type CuratedCollectionArtworkEntry = z.infer<
  typeof CuratedCollectionArtworkEntry
>;

export const CuratedCollection = BaseCuratedCollection.extend({
  avatar: FileHandle.nullish(),
  banner: FileHandle.nullish(),
  populated: z
    .object({
      createdBy: SimpleUser,
    })
    .nullish(),
});
export type CuratedCollection = z.infer<typeof CuratedCollection>;

export const Collection = BaseCollection.extend({
  // secondary marketplace
  policyIds: z.string().array().nullish(),
  numArtworks: z.number().nullish(),
  numCollectors: z.number().nullish(),
  populated: z
    .object({
      createdBy: SimpleUser,
    })
    .nullish(),
});

export type Collection = z.infer<typeof Collection>;

export const ArtworkMetadataEntry = z.object({
  key: z.string(),
  value: z.string(),
});

export type ArtworkMetadataEntry = z.infer<typeof ArtworkMetadataEntry>;

export const ArtworkMetadata = z.object({
  entries: ArtworkMetadataEntry.array(),
});

export type ArtworkMetadata = z.infer<typeof ArtworkMetadata>;

export const MintingRules = z.object({
  version: z.literal(1),
  numMinting: z.object({ eq: z.number().int().optional() }).nullish(),
  mintFee: z.number().int(),
  numEditions: z.number().int(),
  numMinted: z.number().int(),
});

export type MintingRules = z.infer<typeof MintingRules>;

export const BaseArtwork = z.object({
  artworkId: z.string(), // primary key
  createdBy: UserId,
  collectionId: z.string().nullish(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp.nullish(),
  file: FileHandle, // the photo/video
  imageFile: FileHandle.nullish(), // the asset's thumbnail (for video or image asset), as `image` field in onchainMetadata
  title: z.string(),
  description: z.string(),
  tags: z.string().array(),
  metadata: ArtworkMetadata.nullish(),
  isNSFW: z.boolean(),
  flagPhotosensitivity: z.boolean().nullish(),
  lovedBy: UserId.array(),
  // An artwork is available for minting when there is an accompanied UTxO
  utxo: KreateUTxO.nullish(),
  listedAt: UnixTimestamp.nullish(),
  // An artwork restored from other source
  assetId: z.string().nullish(),
  // early suport assetId for on-demand minting artwork
  __assetId: z.string().nullish(),
  // for md2
  __refUserId: z.string().nullish(),
  __refArtworkId: z.string().nullish(),
  mintingRules: MintingRules.nullish(),
  mintingMethod: MintingMethod.nullish(),
  // auction, manually config this field to start new auction
  englishAuction: z
    .object({
      startedAt: UnixTimestamp.nullish(),
      endedAt: UnixTimestamp.nullish(),
      reserve: z.number().nullish(),
      increment: z.number().nullish(),
      numAuctionedEditions: z.number().nullish(),
    })
    .nullish(),
});
export type BaseArtwork = z.infer<typeof BaseArtwork>;

export const Artwork = BaseArtwork.extend({
  totalNumComments: z.number().nullish(),
  numCollectors: z.number().nullish(),
  totalSupply: z.number().nullish(),
  // return additional information for FE
  populated: z
    .object({
      createdBy: SimpleUser,
      collection: SimpleCollection.nullish(),
    })
    .nullish(),
});
export type Artwork = z.infer<typeof Artwork>;

export const User = BaseUser.extend({
  collections: Collection.array(),
  curatedCollections: CuratedCollection.array().nullish(),
  artworks: Artwork.array(),
  // This array can be truncated to save payloads,
  // since we don't need to display all artworks whenever a User is presented.
  // Hence also the need for the separate `numTotalArtworks` field.
  numTotalArtworks: z.number(),
  numCollectors: z.number().nullish(),
}).omit({
  galleryInfo: true,
});

export type User = z.infer<typeof User>;

export const Comment = z.object({
  commentId: ObjectIdAsHexString, // primary key
  targetId: ObjectIdAsHexString,
  type: z.literal("artwork").default("artwork"),
  authorId: ObjectIdAsHexString,
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
  content: z.string(),
  lovedBy: z.string().array(),
  replyTo: ObjectIdAsHexString.nullish(),
});

export type Comment = z.infer<typeof Comment>;

export const CommentHighlight = Comment.extend({
  populated: z.object({
    author: SimpleUser,
    artwork: Artwork,
  }),
});

export type CommentHighlight = z.infer<typeof CommentHighlight>;

export const PlatformHighlight = z.object({
  featuredArtwork: Artwork.nullish(),
  topArtworks: Artwork.array().nullish(),
  topArtists: User.array().nullish(),
  emergingArtists: User.array().nullish(),
  ourPickedComments: CommentHighlight.array().nullish(),
});

export type PlatformHighlight = z.infer<typeof PlatformHighlight>;

export const PlatformCountingStats = z.object({
  numUsers: z.number().nullish(),
  numArtists: z.number().nullish(),
  numArtworks: z.number().nullish(),
  numComments: z.number().nullish(),
  numCollectors: z.number().nullish(),
  numLoves: z.number().nullish(),
  // marketplace
  numTransactions: z.number().nullish(),
  totalLovelaceVolume: z.number().nullish(),
  numListings: z.number().nullish(),
  numOffers: z.number().nullish(),
});

export type PlatformCountingStats = z.infer<typeof PlatformCountingStats>;

/** @deprecated use PlatformCountingStats and PlatformHighlight instead */
export const PlatformStats = PlatformHighlight.merge(
  PlatformCountingStats
).extend({
  numMinted: z.number().nullish(),
});

/** @deprecated use PlatformCountingStats and PlatformHighlight instead */
export type PlatformStats = z.infer<typeof PlatformStats>;

export const Highlight = z.object({
  featuredArtworkId: z.string().optional(),
  topArtworkIds: z.string().array(),
  topArtistIds: z.string().array(),
  emergingArtistIds: z.string().array(),
  ourPickedCommentIds: z.string().array(),
});

export type Highlight = z.infer<typeof Highlight>;

/**
 * Restore policies & gallery collector's address
 */
const RestorePolicyJobBase = z.object({
  jobId: ObjectIdAsHexString,
  refJobId: ObjectIdAsHexString.nullish(),
  collectionId: ObjectIdAsHexString,
  policyId: z.string(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
  message: z.string().nullish(),
  sourceDescription: z.string().nullish(),
  priority: z.number().nullish(),
});

const RestoreCollectorGalleryJobBase = RestorePolicyJobBase.omit({
  collectionId: true,
  policyId: true,
}).extend({
  address: z.string(),
});

const RestorePolicyGalleryJobBase = RestorePolicyJobBase.omit({
  collectionId: true,
});

const RestoreSingleGalleryAssetJobBase = RestorePolicyJobBase.omit({
  collectionId: true,
  policyId: true,
});

export const RestoreAssetStatus = z.enum([
  "pending",
  "processing",
  "done",
  "fail",
  "unsupported",
]);
export type RestoreAssetStatus = z.infer<typeof RestoreAssetStatus>;

export const RestorePolicyStatus = z.enum([
  "pending",
  "fetching",
  "processing",
  "stopping",
  "done",
  "fail",
]);
export type RestorePolicyStatus = z.infer<typeof RestorePolicyStatus>;

export const RestorePolicyJob = z.union([
  RestorePolicyJobBase.extend({
    type: z.literal("collection"),
    status: RestorePolicyStatus,
    excludesDeletedArtworks: z.boolean().nullish(),
  }),
  RestorePolicyJobBase.extend({
    type: z.literal("artwork"),
    status: RestoreAssetStatus,
    assetId: z.string(),
  }),
  RestoreCollectorGalleryJobBase.extend({
    type: z.literal("address"),
    status: RestorePolicyStatus,
  }),
  RestorePolicyGalleryJobBase.extend({
    type: z.literal("policy"),
    status: RestorePolicyStatus,
  }),
  RestoreSingleGalleryAssetJobBase.extend({
    type: z.literal("gallery-artwork"),
    status: RestoreAssetStatus,
    assetId: z.string(),
    // done indexed collectors, policyRoyalty
    hasDoneIndexedOtherData: z.boolean().nullish(),
  }),
]);

export type RestorePolicyJob = z.infer<typeof RestorePolicyJob>;

/**
 * Collector
 */

// for primary page (stats counting & collector in artwork)
export const AssetCollectorType = z.enum(["listed-on-kreate", "none"]);
export type AssetCollectorType = z.infer<typeof AssetCollectorType>;

export const AssetCollector = z.object({
  assetId: z.string(),
  assetType: AssetCollectorType.nullish(),
  address: z.string(),
  stakeAddress: z.string().nullish(),
  quantity: z.number(),
  updatedAt: UnixTimestamp,
});
export type AssetCollector = z.infer<typeof AssetCollector>;

// for gallery collector
export const AssetOnCollectorAccount = z.object({
  assetId: z.string(),
  // wallet (stake) address
  address: z.string(),
  quantity: z.number(),
  updatedAt: UnixTimestamp,
  // TODO: (Collector's Gallery) the timestamp when collector buy/mint this asset
  // this is the time when indexer index the asset
  addedAt: UnixTimestamp.nullish(),
  // if the asset is an ada handle, cache the resolved name
  resolvedAdaHandle: z.string().nullish(),
});
export type AssetOnCollectorAccount = z.infer<typeof AssetOnCollectorAccount>;

export const BaseCollector = z.object({
  address: z.string(),
  stakeAddress: z.string().nullish(),
  adaHandles: z.string().array().nullish(),
  updatedAt: UnixTimestamp,
});
export type BaseCollector = z.infer<typeof BaseCollector>;

export const Collector = BaseCollector.extend({
  userId: ObjectIdAsHexString.nullish(),
  numOfEditions: z.number().nullish(),
  populated: z
    .object({
      userProfile: SimpleUser.nullish(),
      galleryInfo: GalleryInfo.nullish(),
    })
    .nullish(),
}).omit({
  updatedAt: true,
});
export type Collector = z.infer<typeof Collector>;

// Temporary collection of collector indexing tasks
export const CollectorIndexingJob = z.object({
  assetIds: z.string().array(),
  assetType: AssetCollectorType,
  createdFrom: z.enum(["restore-policies", "index-collector-cron"]),
  createdAt: UnixTimestamp,
});
export type CollectorIndexingJob = z.infer<typeof CollectorIndexingJob>;

// Challenge
export const NonceUseCase = z.enum(["login", "link-wallet", "authenticate"]);
export type NonceUseCase = z.infer<typeof NonceUseCase>;

export const NonceToken = z.object({
  nonce: NonceTokenString,
  userId: UserId.nullish(),
  useCase: NonceUseCase,
  isUsed: z.boolean(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});
export type NonceToken = z.infer<typeof NonceToken>;

// Auction
export const BaseAuctionBid = z.object({
  txHash: z.string(),
  blockTime: UnixTimestamp,
  bidderAddress: z.string(),
  bidAmount: z.number(),
  assets: z.string().array(),
  datum: KreateDatum.nullish(),
});
export type BaseAuctionBid = z.infer<typeof BaseAuctionBid>;

export const AuctionBid = BaseAuctionBid.extend({
  placedAt: UnixTimestamp,
  adaHandles: z.string().array().nullish(),
  userId: ObjectIdAsHexString.nullish(),
  populated: z
    .object({
      userProfile: SimpleUser.nullish(),
    })
    .nullish(),
}).omit({
  blockTime: true,
});
export type AuctionBid = z.infer<typeof AuctionBid>;

// gacha minting
export const GachaToken = z.object({
  tokenName: z.string(),
  policyId: z.string(),
  numEditions: z.number(),
});
export type GachaToken = z.infer<typeof GachaToken>;

export const GachaMintingTransaction = z.object({
  offerTxId: z.string(),
  fulfillTxId: z.string(),
  resultStatus: z.enum(["fulfilled", "refunded", "rejected", "none"]),
  tokens: GachaToken.array().nullish(),
  offererAddress: z.string(),
  finished: z.boolean().nullish(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});
export type GachaMintingTransaction = z.infer<typeof GachaMintingTransaction>;

// ribbon highlight
export const BaseHighlightRibbon = z.object({
  hidden: z.boolean().nullish(),
  theme: z.enum(["lightBackground", "darkBackground"]).nullish(),
  backgroundLayers: z.string().array().nullish(),
  backgroundImage: z
    .enum(["eins", "zwei", "drei", "vier", "funf", "sechs", "sieben"])
    .nullish(),
});
export type BaseHighlightRibbon = z.infer<typeof BaseHighlightRibbon>;

export const HighlightRibbon = z.union([
  BaseHighlightRibbon.extend({
    type: z.literal("english-auction"),
    artworkId: ObjectIdAsHexString,
  }),
  BaseHighlightRibbon.extend({
    type: z.literal("gacha-minting"),
    collectionId: ObjectIdAsHexString,
  }),
  BaseHighlightRibbon.extend({
    type: z.literal("catalyst-voting"),
  }),
  BaseHighlightRibbon.extend({
    type: z.literal("curated-collection"),
    collectionId: ObjectIdAsHexString,
  }),
]);
export type HighlightRibbon = z.infer<typeof HighlightRibbon>;

// address tracking
export const AddressTracking = z.object({
  type: z.literal("last-tx-block"),
  address: z.string(),
  block: z.number(),
  tx_hash: z.string(),
  total: z.number(),
  updatedAt: UnixTimestamp,
  // experiment, set = true when total transactions on db is not matched with blockfrost
  __anomalyDetected: z.boolean().nullish(),
});
export type AddressTracking = z.infer<typeof AddressTracking>;

// Event Changes
export const EventTrackingEntry = z.object({
  waitForSending: z.boolean(),
  sentAt: UnixTimestamp.nullish(),
  disabled: z.boolean().nullish(),
  reason: z.string().nullish(),
  messageId: z.string().nullish(),
  retries: z.number().nullish(),
});
export type EventTrackingEntry = z.infer<typeof EventTrackingEntry>;

export const EventTracking = z.object({
  discord: EventTrackingEntry.nullish(),
  email: EventTrackingEntry.nullish(),
  emailSubscribers: EventTrackingEntry.nullish(),
});
export type EventTracking = z.infer<typeof EventTracking>;

export const BaseEventChanges = z.object({
  eventId: ObjectIdAsHexString,
  // the owner who create the event
  userId: ObjectIdAsHexString.nullish(),
  // the subscribers related to the event (It's mainly the users in reply threads, the bidder, buyer ...)
  subscribers: ObjectIdAsHexString.array().nullish(),
  createdAt: UnixTimestamp,
  scheduled: z.boolean(),
  eventTracking: EventTracking.nullish(),
});
export type BaseEventChanges = z.infer<typeof BaseEventChanges>;

export const EventSale = z.object({
  assetId: z.string(),
  type: z.enum(["artwork", "marketplace", "secondary"]),
  txId: z.string(),
  // blockTime: z.number().nullish(),
  price: z.number(),
  numEditionsSold: z.number(),
  numEditionsLeft: z.number(),
  sellerAddress: z.string().nullish(),
  collectorAddress: z.string().nullish(),
  txMsg: z.string().nullish(),
  // for secondary
  artistUserId: ObjectIdAsHexString.nullish(),
  artistAddress: z.string().nullish(),
  royalty: z.number().nullish(),
});
export type EventSale = z.infer<typeof EventSale>;

export const EventSaleRoyalty = z.object({
  assetId: z.string(),
  txId: z.string(),
  price: z.number(),
  sellerAddress: z.string(),
  collectorAddress: z.string().nullish(),
  royalty: z.number(), // price * royalty rate
  numEditionsSold: z.number(),
});
export type EventSaleRoyalty = z.infer<typeof EventSaleRoyalty>;

export const EventOffer = z.object({
  type: z.enum(["created", "fulfilled", "cancelled"]),
  assetId: z.string(),
  txId: z.string(),
  blockTime: z.number().nullish(),
  price: z.number(),
  numEditionsFulfilled: z.number(),
  numEditionsLeft: z.number(),
  sellerAddress: z.string().nullish(),
  offererAddress: z.string().nullish(),
  txMsg: z.string().nullish(),
  // for secondary
  artistUserId: ObjectIdAsHexString.nullish(),
  artistAddress: z.string().nullish(),
  royalty: z.number().nullish(),
});
export type EventOffer = z.infer<typeof EventOffer>;

export const EventGachaMinting = z.object({
  artworkId: ObjectIdAsHexString,
  txId: z.string(),
  collectorAddress: z.string().nullish(),
});
export type EventGachaMinting = z.infer<typeof EventGachaMinting>;

export const EventComment = z.object({
  commentId: ObjectIdAsHexString,
});
export type EventComment = z.infer<typeof EventComment>;

export const EventAuctionNewBid = z.object({
  assetId: z.string(),
  txId: z.string(),
  price: z.number(),
  bidderAddress: z.string(),
});
export type EventAuctionNewBid = z.infer<typeof EventAuctionNewBid>;

export const EventAuctionEnded = z.object({
  assetId: z.string(),
  endedAt: UnixTimestamp,
  winnerAddress: z.string().nullish(),
  winnerTxId: z.string().nullish(),
  price: z.number().nullish(),
});
export type EventAuctionEnded = z.infer<typeof EventAuctionEnded>;

export const AllEventChangesType = [
  "sale",
  "sale_royalty",
  "comment",
  "gacha_minting",
  "offer",
  "auction_newbid",
  "auction_ended",
];

export const EventChanges = z.union([
  BaseEventChanges.extend({
    type: z.literal("sale"),
    data: EventSale,
  }),
  BaseEventChanges.extend({
    type: z.literal("sale_royalty"),
    data: EventSaleRoyalty,
  }),
  BaseEventChanges.extend({
    type: z.literal("comment"),
    data: EventComment,
  }),
  BaseEventChanges.extend({
    type: z.literal("gacha_minting"),
    data: EventGachaMinting,
  }),
  BaseEventChanges.extend({
    type: z.literal("offer"),
    data: EventOffer,
  }),
  BaseEventChanges.extend({
    type: z.literal("auction_newbid"),
    data: EventAuctionNewBid,
  }),
  BaseEventChanges.extend({
    type: z.literal("auction_ended"),
    data: EventAuctionEnded,
  }),
]);
export type EventChanges = z.infer<typeof EventChanges>;

// Collector Gallery
export const ArtworkRawMetadataEntry = z.object({
  key: z.string(),
  value: z.unknown(),
});
export type ArtworkRawMetadataEntry = z.infer<typeof ArtworkRawMetadataEntry>;

export const BaseGalleryArtwork = z.object({
  /** @deprecated - use `assetId` as primary key insteads */
  galleryArtworkId: ObjectIdAsHexString,
  assetId: z.string(), // primary key
  title: z.string(),
  description: z.string().nullish(),
  policyId: z.string(),
  assetFingerprint: z.string().nullish(),
  mintingTokenName: z.string(),
  metadata: z
    .object({
      entries: ArtworkRawMetadataEntry.array(),
    })
    .nullish(),
  fileId: z.string(),
  imageFileId: z.string().nullish(),
  otherFileIds: z.string().array().nullish(),
  // totalSupply on chain
  totalSupply: z.number().nullish(),
  // minted at time
  assetMintedAt: UnixTimestamp.nullish(),
  syncedAt: UnixTimestamp.nullish(),
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
  // for filtering by traits
  traitKVPairs: z.string().array().nullish(),
});
export type BaseGalleryArtwork = z.infer<typeof BaseGalleryArtwork>;

export const GalleryArtwork = BaseGalleryArtwork.extend({
  file: FileHandle,
  imageFile: FileHandle.nullish(),
  otherFiles: FileHandle.array().nullish(),
  // if owner's address was provided (in case owner logged-in or user visited the owner's gallery page),
  // return total quantity of an asset owned by all owner's address list,
  // else return quantity = null
  /** @deprecated - use `numEditionsOwnedByCollector` and `numEditionsOwnedByActor` insteads */
  quantity: z.number().nullish(),
  // if owner's address was provided (in case owner logged-in or user visited the owner's gallery page),
  // this is the albums of this owner that this asset has been added to, else this will be set to undefined
  galleryAlbumIds: ObjectIdAsHexString.array().nullish(),
  // if owner's address was provided (in case owner logged-in or user visited the owner's gallery page),
  // this is the hidden status of the assets, else return `undefined`
  hidden: z.boolean().nullish(),
  // The `priority` in current filtered album. On total assets list page, when album hasn't been provided, `priority` = undefined
  priority: z.number().nullish(),
  addedAt: UnixTimestamp.nullish(),
  kreateArtwork: Artwork.nullish(),
  // royalty info
  policyRoyalty: PolicyRoyalty.nullish(),
  // utxo of the asset if listed
  /** @deprecated - use `bestListingUtxoByCollector` insteads */
  listingUTxO: KreateUTxO.nullish(),
  bestListingUtxoByCollector: KreateUTxO.nullish(),
  numEditionsOwnedByCollector: z.number().nullish(),
  numEditionsOwnedByActor: z.number().nullish(),
  // num of listings and offers
  numListings: z.number().nullish(),
  numOffers: z.number().nullish(),
}).omit({
  fileId: true,
  imageFileId: true,
  otherFileIds: true,
  traitKVPairs: true,
});
export type GalleryArtwork = z.infer<typeof GalleryArtwork>;

export const BaseGalleryAlbum = z.object({
  galleryAlbumId: z.string(), // primary key
  createdBy: UserId,
  createdAt: UnixTimestamp,
  updatedAt: UnixTimestamp.nullish(),
  title: z.string(),
  description: z.string().nullish(),
  handle: GalleryAlbumUrlHandleString.nullish(),
  lovedBy: z.string().array().nullish(),
  priority: z.number().nullish(),
});
export type BaseGalleryAlbum = z.infer<typeof BaseGalleryAlbum>;

export const GalleryAlbum = BaseGalleryAlbum.extend({
  avatarFiles: FileHandle.array().nullish(),
  ogImage: FileHandle.nullish(),
  totalNumArtworks: z.number(),
});
export type GalleryAlbum = z.infer<typeof GalleryAlbum>;

export const SimpleGalleryAlbum = GalleryAlbum.pick({
  galleryAlbumId: true,
  handle: true,
  title: true,
});
export type SimpleGalleryAlbum = z.infer<typeof SimpleGalleryAlbum>;

export const GalleryCollector = z.object({
  // backward compatible
  // TODO: remove outdated fields
  /** @deprecated use `addresses` instead */
  address: z.string().default(""),
  /** @deprecated use `addresses` instead */
  adaHandles: z.string().array().nullish(),
  // for collector's gallery
  addresses: z
    .object({
      address: z.string(),
      adaHandles: z.string().array().nullish(),
    })
    .array(),
  isCollectorAnArtist: z.boolean().nullish(),
  // preview
  ogImage: FileHandle.nullish(),
  // aggregate data
  totalNumHiddenArtworks: z.number().nullish(),
  totalNumUnsortedArtworks: z.number().nullish(),
  totalNumArtworks: z.number().nullish(),
  totalNumForSaleArtworks: z.number().nullish(),
  totalNumSentOffers: z.number().nullish(),
  totalNumReceivedOffers: z.number().nullish(),
  // sync status
  gallerySyncedAt: UnixTimestamp.nullish(),
  gallerySyncedStatus: z.enum(["pending", "processing", "done"]).nullish(),
  populated: z
    .object({
      userProfile: SimpleUser.nullish(),
      galleryInfo: GalleryInfo.nullish(),
      landingAlbum: SimpleGalleryAlbum.nullish(),
    })
    .nullish(),
});
export type GalleryCollector = z.infer<typeof GalleryCollector>;

export const SimpleGalleryCollector = GalleryCollector.pick({
  addresses: true,
  populated: true,
});
export type SimpleGalleryCollector = z.infer<typeof SimpleGalleryCollector>;

export const HighlightGalleryCollector = GalleryCollector.pick({
  addresses: true,
  populated: true,
  totalNumArtworks: true,
}).extend({
  totalNumAlbums: z.number().nullish(),
});
export type HighlightGalleryCollector = z.infer<
  typeof HighlightGalleryCollector
>;

export const SecondaryListingEntry = z.object({
  utxo: KreateUTxO,
  populated: z
    .object({
      galleryCollector: SimpleGalleryCollector.nullish(),
    })
    .nullish(),
});
export type SecondaryListingEntry = z.infer<typeof SecondaryListingEntry>;

export const SecondaryOfferEntry = SecondaryListingEntry;
export type SecondaryOfferEntry = z.infer<typeof SecondaryOfferEntry>;

export const GalleryOffer = z.object({
  utxo: KreateUTxO,
  populated: z
    .object({
      galleryCollector: SimpleGalleryCollector.nullish(),
      galleryArtwork: GalleryArtwork.nullish(),
    })
    .nullish(),
});
export type GalleryOffer = z.infer<typeof GalleryOffer>;

// marketplace UI
export const TraitFilterData = z.object({
  key: z.string(),
  /** @deprecated use `valueList.value` instead */
  values: z.string().array(),
  valueList: z
    .object({
      value: z.string(),
      count: z.number(),
    })
    .array(),
});
export type TraitFilterData = z.infer<typeof TraitFilterData>;

export const SecondaryArtwork = BaseGalleryArtwork.extend({
  file: FileHandle,
  imageFile: FileHandle.nullish(),
  bestListingUtxO: KreateUTxO.nullish(),
  populated: z
    .object({
      kreateArtwork: z
        .object({
          artworkId: ObjectIdAsHexString,
          utxo: KreateUTxO.nullish(),
        })
        .nullish(),
      artistProfile: SimpleUser.nullish(),
      // use artistName if `populated.artistProfile` is null
      policy: z
        .object({
          artistName: z.string().nullish(),
        })
        .nullish(),
    })
    .nullish(),
}).omit({
  fileId: true,
  imageFileId: true,
  traitKVPairs: true,
});
export type SecondaryArtwork = z.infer<typeof SecondaryArtwork>;

export const SimpleSecondaryArtwork = SecondaryArtwork.pick({
  file: true,
  imageFile: true,
  title: true,
  description: true,
  assetId: true,
  assetFingerprint: true,
  policyId: true,
  mintingTokenName: true,
});
export type SimpleSecondaryArtwork = z.infer<typeof SimpleSecondaryArtwork>;

export const BasePolicyStats = z.object({
  floorPrice: z.number().nullish(),
  totalVolume: z.number().nullish(),
  // count including editions
  totalSupply: z.number().nullish(),
  // count only asset
  numAssets: z.number().nullish(),
});
export type BasePolicyStats = z.infer<typeof BasePolicyStats>;

export const BasePolicyDetailInfo = z.object({
  policyId: z.string(), // use asd key
  // link to primary collectionId (for faster exploring policies)
  primaryCollectionId: ObjectIdAsHexString.nullish(),
  // if artist already have profile on Kreate, and haven't imported the policy to Kreate
  artistProfileId: ObjectIdAsHexString.nullish(),
  // other artist info
  artistName: z.string().nullish(),
  // collection info
  avatarFileId: ObjectIdAsHexString.nullish(),
  bannerFileId: ObjectIdAsHexString.nullish(),
  title: z.string().nullish(),
  description: z.string().nullish(),
  isVerified: z.boolean().nullish(),

  // calc from kreate platform
  totalNumListedEditions: z.number().nullish(),
  bestOffer: z.number().nullish(),
  royalty: z.number().nullish(),
  // stats from onchain data
  stats: BasePolicyStats.nullish(),
  // config trait
  traits: z.string().array().nullish(),
  // manually highlight policy in the first page
  sortIndex: z.number().nullish(),
  // lookup this value from kreate database
  ownerStakeAddress: z.string().nullish(),

  // last time imported assets from onchain
  lastSyncAssetsAt: UnixTimestamp.nullish(),
});
export type BasePolicyDetailInfo = z.infer<typeof BasePolicyDetailInfo>;

export const PolicyDetailInfo = BasePolicyDetailInfo.extend({
  // backward compatibility, these information is duplicated with `stats`
  totalVolume: z.number().nullish(),
  floorPrice: z.number().nullish(),
  floorPriceOnKreate: z.number().nullish(),
  quantity: z.number().nullish(),
  // base data
  avatar: FileHandle.nullish(),
  banner: FileHandle.nullish(),
  isUnsortedCollection: z.boolean().nullish(),
  populated: z.object({
    collection: SimpleCollection.nullish(),
    userProfile: SimpleUser.nullish(),
    topArtworks: SimpleSecondaryArtwork.array().nullish(),
  }),
});
export type PolicyDetailInfo = z.infer<typeof PolicyDetailInfo>;

export const SimplePolicyDetailInfo = PolicyDetailInfo.pick({
  policyId: true,
  avatar: true,
  banner: true,
  title: true,
  description: true,
  isVerified: true,
  artistName: true,
  populated: true,
  isUnsortedCollection: true,
});
export type SimplePolicyDetailInfo = z.infer<typeof SimplePolicyDetailInfo>;

export const BaseSecondaryHighlight = z.object({
  pickedPolicyIds: z.string().array().nullish(),
  trendingPolicyIds: z.string().array().nullish(),
  sponsoredPolicyId: z.string().nullish(),
  featuredPolicyId: z.string().nullish(),
  topGalleryAddresseOrHandles: z.string().array().nullish(),
});
export type BaseSecondaryHighlight = z.infer<typeof BaseSecondaryHighlight>;

export const SecondaryHighlight = z.object({
  pickedCollections: PolicyDetailInfo.array().nullish(),
  trendingCollections: PolicyDetailInfo.array().nullish(),
  sponsoredCollection: PolicyDetailInfo.nullish(),
  featuredCollection: PolicyDetailInfo.nullish(),
  topGalleries: HighlightGalleryCollector.array().nullish(),
});
export type SecondaryHighlight = z.infer<typeof SecondaryHighlight>;

// transaction tracking
export const Transaction = z.object({
  txHash: z.string(),
  txIndex: z.number(),
  blockHeight: z.number(),
  blockTime: z.number(),
  indexTime: UnixTimestamp.nullish(),
  addresses: z.string().array().nullish(),
});

export type Transaction = z.infer<typeof Transaction>;

// volume counting
export const TotalAggregationVolume = z.object({
  lastComputedAt: UnixTimestamp.nullish(),
  data: z.union([
    z.object({
      type: z.literal("total"),
      lovelace: z.number(),
      lastBlock: z.number(),
      lastIndex: z.number(),
      // = lovelace + lovelace of the new transactions which have not persist to db yet
      realLovelace: z.number().nullish(),
    }),
    z.object({
      type: z.literal("policy"),
      policyId: z.string(),
      lovelace: z.number(),
      // = lovelace + lovelace of the new transactions which have not persist to db yet
      realLovelace: z.number().nullish(),
    }),
  ]),
});
export type TotalAggregationVolume = z.infer<typeof TotalAggregationVolume>;

// cron job result
export const CronJobResult = z.object({
  size: z.number().nullish(),
  partial: z.boolean().nullish(),
  metrics: z.record(z.string(), z.number()).nullish(),
});
export type CronJobResult = z.infer<typeof CronJobResult>;
